/* istanbul ignore file */
import Immutable, { type Immutable as TImmutable } from 'seamless-immutable'

type GeneratedActions<TName extends string> = {
  [K in TName]: ['args']
} & {
  [K in `${TName}Request`]: null
} & {
  [K in `${TName}Success`]: ['data']
} & {
  [K in `${TName}Failure`]: ['error']
}

export const actionGenerator = <const TName extends string>(
  baseName: TName
): GeneratedActions<TName> =>
  ({
    [`${baseName}`]: ['args'],
    [`${baseName}Request`]: null,
    [`${baseName}Success`]: ['data'],
    [`${baseName}Failure`]: ['error'],
  } as unknown as GeneratedActions<TName>)

type BaseSliceType = { requests: Record<string, unknown> }
type RequestActionReducer<TState extends BaseSliceType> = (
  state: TImmutable<TState>
) => TImmutable<TState>
type SuccessActionReducer<TState extends BaseSliceType> = (
  state: TImmutable<TState>,
  args: { data: any }
) => TImmutable<TState>
type FailureActionReducer<TState extends BaseSliceType> = (
  state: TImmutable<TState>,
  args: { error: any }
) => TImmutable<TState>

export const handlerGenerator = <TState extends BaseSliceType>(
  baseName: string,
  startReducer: RequestActionReducer<TState> | null,
  successReducer: SuccessActionReducer<TState> | null,
  failureReducer: FailureActionReducer<TState> | null
) => {
  const request = (state: TImmutable<TState>) => {
    const newState = Immutable({
      ...state,
      requests: {
        ...state.requests,
        [baseName]: {
          status: 'request',
        },
      },
    } as TState)
    return startReducer ? startReducer(newState) : newState
  }
  const success = (state: TImmutable<TState>, action: { data: unknown }) => {
    const newState = Immutable({
      ...state,
      requests: {
        ...state.requests,
        [baseName]: {
          status: 'success',
        },
      },
    } as TState)
    return successReducer ? successReducer(newState, action) : newState
  }
  const failure = (state: TImmutable<TState>, action: { error: unknown }) => {
    const newState = Immutable({
      ...state,
      requests: {
        ...state.requests,
        [baseName]: {
          status: 'error',
          error: action.error,
        },
      },
    } as TState)
    return failureReducer ? failureReducer(newState, action) : newState
  }

  return {
    [`${baseName}_REQUEST`]: request,
    [`${baseName}_SUCCESS`]: success,
    [`${baseName}_FAILURE`]: failure,
  }
}

export default {
  actionGenerator,
  handlerGenerator,
}
